<script setup>
/**
 * OCHAT: el calendario de ocupación. Es la pantalla que el equipo del hotel mira
 * todos los días.
 *
 * MUESTRA DISPONIBILIDAD, NO RESERVAS
 * Es la diferencia que hace que sirva. Una cuadrícula de reservas contesta "quién
 * viene"; esta contesta "¿puedo vender?", que es la pregunta del mostrador cuando
 * suena el teléfono. Por eso cada celda es el resultado de la consulta de
 * disponibilidad para esa noche, y no un bloque por reserva.
 *
 * Las fechas se manejan como cadenas ISO y no como Date. Una noche es una fecha en
 * el hotel: pasarla por Date la ata a la zona del navegador y la corre un día para
 * cualquiera que mire desde otro huso.
 */
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const store = useStore();
const uiFlags = useMapGetter('bookings/getUIFlags');

const DAYS = 14;

/** Hoy en ISO, sin pasar por Date para no arrastrar la zona del navegador. */
const todayISO = () => new Date().toLocaleDateString('en-CA');

const addDays = (iso, count) => {
  // Se usa UTC a propósito: solo interesa avanzar el calendario, y en UTC no hay
  // horario de verano que haga que un día tenga 23 o 25 horas.
  const [y, m, d] = iso.split('-').map(Number);
  const base = Date.UTC(y, m - 1, d);
  return new Date(base + count * 86400000).toISOString().slice(0, 10);
};

const from = ref(todayISO());
const availability = ref(null);

const dates = computed(() =>
  Array.from({ length: DAYS }, (_, index) => addDays(from.value, index))
);

// Una consulta por noche: la disponibilidad de una noche suelta es el mismo cálculo
// que la de un rango de una noche, así se reusa el motor sin endpoints nuevos.
const load = async () => {
  const results = await Promise.all(
    dates.value.map(date =>
      store
        .dispatch('bookings/checkAvailability', {
          checkin: date,
          checkout: addDays(date, 1),
        })
        .catch(() => null)
    )
  );

  const byRoomType = new Map();
  results.forEach((result, index) => {
    const date = dates.value[index];
    (result?.offers ?? []).forEach(offer => {
      if (!byRoomType.has(offer.room_type_id)) {
        byRoomType.set(offer.room_type_id, {
          id: offer.room_type_id,
          name: offer.room_type_name,
          nights: {},
        });
      }
      // Varios planes de tarifa comparten el inventario del tipo, así que alcanza
      // con el primero que reporte: `bookable` es del tipo, no del plan.
      const row = byRoomType.get(offer.room_type_id);
      if (row.nights[date] === undefined) {
        row.nights[date] = { bookable: offer.bookable, price: offer.total };
      }
    });

    (result?.unavailable ?? []).forEach(rejection => {
      if (!rejection.room_type_id) return;
      if (!byRoomType.has(rejection.room_type_id)) {
        byRoomType.set(rejection.room_type_id, {
          id: rejection.room_type_id,
          name: rejection.room_type_name,
          nights: {},
        });
      }
      const row = byRoomType.get(rejection.room_type_id);
      if (row.nights[date] === undefined) {
        row.nights[date] = { bookable: 0, reason: rejection.reason };
      }
    });
  });

  availability.value = [...byRoomType.values()];
};

onMounted(load);

const shift = count => {
  from.value = addDays(from.value, count);
  load();
};

const dayLabel = iso => {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
};

const weekdayLabel = iso => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-AR', {
    weekday: 'short',
    timeZone: 'UTC',
  });
};

const isWeekend = iso => {
  const [y, m, d] = iso.split('-').map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return weekday === 0 || weekday === 6;
};

// El color dice qué hacer de un pantallazo: sin lugar en rojo, poco en ámbar,
// disponible en verde. Los umbrales son deliberadamente crudos —lo que importa es
// distinguir "quedan pocas" de "hay de sobra".
const cellClass = night => {
  if (!night) return 'bg-n-slate-2 text-n-slate-10';
  if (night.bookable <= 0) return 'bg-n-ruby-3 text-n-ruby-11';
  if (night.bookable <= 2) return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-teal-3 text-n-teal-11';
};

const cellText = night => {
  if (!night) return '·';
  if (night.bookable > 0) return String(night.bookable);
  // Cerrado y agotado se ven distinto: uno es una decisión del hotel, el otro es
  // que se vendió.
  return night.reason === 'closed' ? '×' : '0';
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium rounded-lg outline outline-1 outline-n-container text-n-slate-11 hover:text-n-slate-12"
        @click="shift(-DAYS)"
      >
        {{ $t('BOOKINGS.CALENDAR.PREVIOUS') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium rounded-lg outline outline-1 outline-n-container text-n-slate-11 hover:text-n-slate-12"
        @click="shift(DAYS)"
      >
        {{ $t('BOOKINGS.CALENDAR.NEXT') }}
      </button>
      <input
        v-model="from"
        type="date"
        class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        @change="load"
      />
    </div>

    <p v-if="uiFlags.isCheckingAvailability" class="text-sm text-n-slate-11">
      {{ $t('BOOKINGS.CALENDAR.LOADING') }}
    </p>

    <p v-else-if="!availability?.length" class="py-8 text-sm text-n-slate-11">
      {{ $t('BOOKINGS.CALENDAR.EMPTY') }}
    </p>

    <div v-else class="overflow-x-auto rounded-lg outline outline-1 outline-n-container">
      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="sticky px-4 py-2 font-medium text-start text-n-slate-11 bg-n-background ltr:left-0 rtl:right-0 min-w-40"
            >
              {{ $t('BOOKINGS.CALENDAR.ROOM_TYPE') }}
            </th>
            <th
              v-for="date in dates"
              :key="date"
              class="px-2 py-2 font-medium text-center text-n-slate-11 min-w-14"
              :class="{ 'bg-n-slate-2': isWeekend(date) }"
            >
              <div>{{ weekdayLabel(date) }}</div>
              <div class="text-n-slate-12">{{ dayLabel(date) }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in availability"
            :key="row.id"
            class="border-b border-n-weak last:border-0"
          >
            <td
              class="sticky px-4 py-2 font-medium bg-n-background text-n-slate-12 ltr:left-0 rtl:right-0"
            >
              {{ row.name }}
            </td>
            <td v-for="date in dates" :key="date" class="p-1 text-center">
              <span
                class="inline-flex items-center justify-center w-full py-1 text-xs font-medium rounded"
                :class="cellClass(row.nights[date])"
              >
                {{ cellText(row.nights[date]) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex flex-wrap items-center gap-4 text-xs text-n-slate-11">
      <span class="flex items-center gap-1.5">
        <span class="w-3 h-3 rounded bg-n-teal-3" />
        {{ $t('BOOKINGS.CALENDAR.LEGEND.AVAILABLE') }}
      </span>
      <span class="flex items-center gap-1.5">
        <span class="w-3 h-3 rounded bg-n-amber-3" />
        {{ $t('BOOKINGS.CALENDAR.LEGEND.LOW') }}
      </span>
      <span class="flex items-center gap-1.5">
        <span class="w-3 h-3 rounded bg-n-ruby-3" />
        {{ $t('BOOKINGS.CALENDAR.LEGEND.SOLD_OUT') }}
      </span>
      <span>{{ $t('BOOKINGS.CALENDAR.LEGEND.CLOSED') }}</span>
    </div>
  </div>
</template>
